import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { AboutModal, ScheduleModal } from "../components/Modals";
import BlockchainBadge from "../components/BlockchainBadge";
import AdSlot from "../components/AdSlot";
import { trackPageView } from "../lib/analytics";

function formatLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return iso;
  }
}

function TimelineNode({ post, side }) {
  const deleted = post.is_deleted;
  const verifyUrl = post.opentimestamps_verification_url
    || (post.blockchain_hash ? `https://opentimestamps.org/?digest=${post.blockchain_hash}` : null);
  return (
    <div
      className={`xs-tl-row ${side === "left" ? "left" : "right"}`}
      data-testid={`timeline-row-${post.slug}`}
    >
      <div className="xs-tl-dot" aria-hidden="true" />
      <div className="xs-tl-card" data-testid={`timeline-card-${post.slug}`}>
        <div className="xs-tl-time" data-testid={`timeline-time-${post.slug}`}>
          {formatLocal(post.published_at)}
        </div>
        {deleted ? (
          <div className="xs-tl-deleted">
            <h3 style={{ margin: "4px 0 8px", color: "#ffde16", textDecoration: "line-through", opacity: 0.65 }}>
              {post.title}
            </h3>
            <div style={{ fontSize: "0.85em", color: "#ff9b6b", marginBottom: 8 }}>
              deleted on <strong>{formatLocal(post.deleted_at)}</strong>
            </div>
            <div style={{ fontSize: "0.75em", opacity: 0.7 }}>
              content removed — original blockchain proof preserved.
            </div>
          </div>
        ) : (
          <>
            {post.cover_image_url && (
              <Link to={`/articles/${post.slug}`}>
                <img
                  src={mediaUrl(post.cover_image_url)}
                  alt=""
                  className="xs-tl-cover"
                  loading="lazy"
                />
              </Link>
            )}
            <h3 className="xs-tl-title">
              <Link to={`/articles/${post.slug}`}>{post.title}</Link>
            </h3>
            {post.excerpt && <p className="xs-tl-excerpt">{post.excerpt}</p>}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {post.tags?.slice(0, 3).map((t) => (
                <span key={t} className="xs-tag">#{t}</span>
              ))}
            </div>
          </>
        )}
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: "0.75em" }}>
          <BlockchainBadge hash={post.blockchain_hash} status={post.opentimestamps_status} compact />
          {verifyUrl && (
            <a href={verifyUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
              verify on bitcoin ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Timeline() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    trackPageView("/posts");
    api.get("/posts/timeline", { params: { limit: 200 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="timeline-page">
        <div className="xs-container">
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h1 style={{ color: "#ffde16", fontSize: "clamp(1.6em, 4vw, 2.6em)", margin: "10px 0 4px", letterSpacing: 2, textTransform: "lowercase" }}>
              the timeline
            </h1>
            <div style={{ opacity: 0.75, fontSize: "0.9em" }}>
              every post — preserved on bitcoin, ordered in time.
            </div>
          </div>

          <AdSlot slot="timeline-top" label="advertisement" />

          {loading && <div style={{ opacity: 0.6, textAlign: "center", padding: 40 }} data-testid="timeline-loading">loading…</div>}
          {!loading && items.length === 0 && (
            <div style={{ opacity: 0.7, textAlign: "center", padding: 40, border: "1px dashed #ffde16", borderRadius: 8 }}>
              no posts yet.
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="xs-timeline" data-testid="timeline-container">
              <div className="xs-tl-spine" aria-hidden="true" />
              {items.map((p, i) => (
                <TimelineNode key={p.id} post={p} side={i % 2 === 0 ? "left" : "right"} />
              ))}
            </div>
          )}
        </div>
      </main>
      {modal === "about" && <AboutModal onClose={() => setModal(null)} />}
      {modal === "schedule" && <ScheduleModal onClose={() => setModal(null)} />}
      <Footer show />
    </div>
  );
}
